// src/features/TeamChat/components/TeamPanel.tsx
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent } from 'react'

import type { TeamMember, TeamSummary } from 'features/TeamChat/api/teamChat.types'
import { TEAM_COPY } from 'features/TeamChat/teamChat.constants'
import styles from 'features/TeamChat/teamChat.module.scss'

import { ChatTab } from './chat/ChatTab'
import { RequestsTab } from './requests/RequestsTab'
import { BellIcon, BellOffIcon, ChevronDownIcon } from './TeamIcons'
import { TeamTabs } from './TeamTabs'

type Tab = 'chat' | 'requests'
type Props = {
  summary?: TeamSummary
  members: TeamMember[]
  timeZone: string
  muted: boolean
  takeFocus: boolean
  onToggleMute: () => void
  onFocused: () => void
  onCollapse: () => void
  onDragStart: (e: PointerEvent<HTMLElement>) => void
}

const stopDrag = (e: PointerEvent) => e.stopPropagation()

const Grip = () => (
  <svg className={styles.grip} width='14' height='20' viewBox='0 0 14 20' aria-hidden='true'>
    {[4, 10, 16].flatMap((cy) =>
      [4, 10].map((cx) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r='1.6' />),
    )}
  </svg>
)

export const TeamPanel = ({
  summary,
  members,
  timeZone,
  muted,
  takeFocus,
  onToggleMute,
  onFocused,
  onCollapse,
  onDragStart,
}: Props) => {
  const [tab, setTab] = useState<Tab>('chat')
  const titleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!takeFocus) return
    titleRef.current?.focus()
    onFocused()
  }, [takeFocus, onFocused])

  // The composer prevents the Escape that closes its open @ chips, so only a second Escape collapses.
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' && !e.defaultPrevented) onCollapse()
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section
      className={`${styles.panel} sentry-mask`}
      data-hj-suppress
      data-team-panel
      aria-label={TEAM_COPY.PANEL_LABEL}
      onKeyDown={onKeyDown}>
      <header className={styles.header} title={TEAM_COPY.DRAG} onPointerDown={onDragStart}>
        <Grip />
        <div ref={titleRef} tabIndex={-1} className={styles.headerText}>
          <span className={styles.headerTitle}>{TEAM_COPY.TITLE}</span>
          <span className={styles.headerSub}>
            {[TEAM_COPY.ONLINE(summary?.online ?? 0), muted && TEAM_COPY.SOUNDS_OFF]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <button
          type='button'
          className={classNames(styles.iconButton, { [styles.iconButtonOn]: muted })}
          aria-pressed={muted}
          aria-label={muted ? TEAM_COPY.UNMUTE : TEAM_COPY.MUTE}
          title={muted ? TEAM_COPY.UNMUTE : TEAM_COPY.MUTE}
          onPointerDown={stopDrag}
          onClick={onToggleMute}>
          {muted ? <BellOffIcon /> : <BellIcon />}
        </button>
        <button
          type='button'
          className={styles.iconButton}
          aria-label={TEAM_COPY.COLLAPSE}
          onPointerDown={stopDrag}
          onClick={onCollapse}>
          <ChevronDownIcon />
        </button>
      </header>
      <TeamTabs<Tab>
        active={tab}
        onChange={setTab}
        tabs={[
          {
            key: 'chat',
            label: TEAM_COPY.CHAT,
            count: summary?.unread_messages ?? 0,
            alert: true,
            panel: <ChatTab members={members} timeZone={timeZone} />,
          },
          {
            key: 'requests',
            label: TEAM_COPY.REQUESTS,
            count: summary?.open_requests ?? 0,
            alert: summary?.mentions_me,
            panel: <RequestsTab members={members} timeZone={timeZone} />,
          },
        ]}
      />
    </section>
  )
}
